import _ from 'lodash'
import { Fragment, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Box, Button, Divider, Grid, IconButton, List, ListItem, ListItemText, Paper, Stack, Tooltip, Typography } from '@mui/material'

import ConfirmationDialog from '@/components/ui/dialogs/ConfirmationDialog'
import PageContainer from '@/components/ui/containers/PageContainer'
import TextInput from '@/components/ui/inputs/TextInput'

import { adminApi } from '@/core/api/admin'
import { useAssignLicensesToStaffMutation, useGetAdminStaffsQuery } from '@/core/services/admin'
import { useGetContractByEstablishmentQuery } from '@/core/services/contract'
import { useGetLicenseQuery } from '@/core/services/license'
import { useRejectLicenseMutation } from '@/core/services/staff'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { selectAuthUser, setAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isAdmin, isStaff } from '@/utils/type-guards'

import LicenseComponentsTable from './components/LicenseComponentsTable'
import PoliciesTable from '@/pages/policies/components/PoliciesTable'
import SelectLicenseesDialog from './components/SelectLicenseesDialog'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

export default function LicenseMonitorPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { licenseId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isTooSmall = useIsMobile()

  const authUser = useAppSelector(selectAuthUser)

  const { data: contract, refetch: refetchContract } = useGetContractByEstablishmentQuery(authUser?.establishment?._id ?? '', {
    skip: !isAdmin(authUser) || !authUser?.establishment,
  })
  const { data: license, isLoading: isFetchingLicense } = useGetLicenseQuery(licenseId ?? '', { skip: !licenseId })
  const { staffs, originalArgs: getAdminStaffsArgs } = useGetAdminStaffsQuery(
    {
      filter: {},
      options: { limit: 0, skip: 0, orderBy: '', order: 'ASC' },
    },
    {
      selectFromResult: ({ data, ...rest }) => ({ staffs: data?.[0], ...rest }),
      skip: !isAdmin(authUser),
    }
  )
  const [assignLicensesToStaff, { isLoading: isAssigningLicense, originalArgs: assignLicensesToStaffArgs }] = useAssignLicensesToStaffMutation()
  const [rejectLicense, { isLoading: isRejectingLicense }] = useRejectLicenseMutation()

  const { staffId: assigningStaffId } = assignLicensesToStaffArgs ?? {}

  const activeStaffs = useMemo(
    () =>
      staffs?.filter((staff) =>
        licenseId ? staff.licenses.includes(licenseId) && !!_.find(contract?.licenseUsages, ['license', licenseId])?.licensees.includes(staff._id) : false
      ) ?? [],
    [contract, licenseId, staffs]
  )

  const licenseesData = useMemo(
    () =>
      staffs?.map((staff) => {
        const licenseUsage = _.find(contract?.licenseUsages, ['license', licenseId])
        const isAssigned = licenseId ? staff.licenses.includes(licenseId) && !!licenseUsage?.licensees.includes(staff._id) : false
        return {
          id: staff._id,
          email: staff.email,
          name: `${staff.userInfo.name} ${staff.userInfo.surname}`,
          isAssigned,
          isAssigning: assigningStaffId === staff._id && isAssigningLicense,
          isDisabled: licenseUsage ? licenseUsage.licensees.length >= licenseUsage.quantity && !licenseUsage.licensees.includes(staff._id) : false,
        }
      }) ?? [],
    [assigningStaffId, contract, isAssigningLicense, licenseId, staffs]
  )

  const licenseComponentsData = useMemo(
    () =>
      license?.components.map((component) => {
        const [type, element] = component.split(':')
        return {
          id: component,
          name: t(`common:licenseComponent.${type}.element.${element}`),
          type: t(`common:licenseComponent.${type}.type`),
        }
      }) ?? [],
    [license, t]
  )

  const policiesData = useMemo(
    () =>
      license?.policies.map((policy) => ({
        id: policy._id,
        description: policy.description,
        name: policy.name,
      })) ?? [],
    [license]
  )

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)

  if (isFetchingLicense) return null

  if (!authUser || !license) return null

  return (
    <PageContainer>
      <Stack direction={isTooSmall ? "column" : "row"} alignItems={isTooSmall ? "flex-end" : "center"} justifyContent="space-between">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <IconButton onClick={() => { navigate('/licenses') }} data-testid="license-monitor-back">
            <ArrowBackIcon color="primary" />
          </IconButton>
          <Typography variant="h2" data-testid="license-monitor-title">{license.name}</Typography>
        </Stack>
        {isAdmin(authUser) && (
          <SelectLicenseesDialog
            licensees={licenseesData}
            onSelectLicensee={async (licenseeId, isSelected) => {
              try {
                const licensee = staffs?.find((staff) => staff._id === licenseeId)
                const assignedLicenses = isSelected ? _.concat(licensee?.licenses ?? [], license._id) : _.without(licensee?.licenses ?? [], license._id)
                const updatedStaff = await assignLicensesToStaff({ staffId: licenseeId, licenses: assignedLicenses }).unwrap()
                if (getAdminStaffsArgs) {
                  // pessimistic update to reflect change immediately
                  dispatch(
                    adminApi.util.updateQueryData('getAdminStaffs', getAdminStaffsArgs, (result) => {
                      const draffStaffs = result[0]
                      for (const draffStaff of draffStaffs) {
                        if (draffStaff._id !== updatedStaff._id) continue
                        Object.assign(draffStaff, updatedStaff)
                        break
                      }
                    })
                  )
                }
                refetchContract()
              } catch (error) {
                enqueueSnackbar(getApiErrorMessage(error, t('notification.staff.assignLicense.error')), { variant: 'error' })
              }
            }}
          />
        )}
        {isStaff(authUser) && (
          <Button
            onClick={() => {
              setOpenConfirmationDialog(true)
            }}
            variant="contained"
            color="error"
          >
            {t('common:button.reject.label')}
          </Button>
        )}
      </Stack>
      <Grid container mt={2} spacing={3}>
        <Grid item xs={12} md={isAdmin(authUser) ? 8 : 12}>
          <Paper>
            <Stack spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
              <Typography component="h2" sx={{ fontSize: '18px', fontWeight: 500, lineHeight: '1.5rem' }}>
                {t('page.licenseMonitor.information.title')}
              </Typography>
              <Typography component="p" sx={{ color: colors.gray500, fontSize: '14px' }} className="mt-1 max-w-2xl text-sm text-gray-500">
                {t('page.licenseMonitor.information.subtitle')}
              </Typography>
            </Stack>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
              <Grid container rowSpacing={4} columnSpacing={2}>
                <Grid item xs={12}>
                  <TextInput fullWidth label={t('field.description.label')} value={license.description} readOnly multiline minRows={3} type="textarea" />
                </Grid>
                {isAdmin(authUser) && (
                  <Grid item xs={6}>
                    <TextInput
                      fullWidth
                      label={t('field.quantity.label')}
                      value={_.find(contract?.licenseUsages, ['license', license._id])?.quantity ?? 0}
                      readOnly
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography component="label" sx={{ color: colors.gray700, fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500 }}>
                    {t('page.licenseMonitor.information.accessibleComponents.label')}
                  </Typography>
                  <Box mt={1}>
                    <LicenseComponentsTable data={licenseComponentsData} />
                  </Box>
                </Grid>
                {isAdmin(authUser) && (
                  <Grid item xs={12}>
                    <Typography component="label" sx={{ color: colors.gray700, fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500 }}>
                      {t('page.licenseMonitor.information.attachedPolicies.label')}
                    </Typography>
                    <Box mt={1}>
                      <PoliciesTable data={policiesData} disableActions disableGlobalSearch selectable={false} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>
        {isAdmin(authUser) && (
          <Grid item xs={12} md={4}>
            <Paper>
              <Stack spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
                <Typography component="h2" sx={{ fontSize: '18px', fontWeight: 500, lineHeight: '1.5rem' }}>
                  {t('page.licenseMonitor.activeStaffs.title')}
                </Typography>
              </Stack>
              {!!activeStaffs.length && (
                <List sx={{ width: '100%', bgcolor: 'background.paper', px: { xs: 2, sm: 3 } }}>
                  {activeStaffs.map((staff, index) => (
                    <Fragment key={staff._id}>
                      <ListItem disableGutters>
                        <ListItemText primary={`${staff.userInfo.name} ${staff.userInfo.surname}`} />
                      </ListItem>
                      {index !== activeStaffs.length - 1 && <Divider component="li" />}
                    </Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
      <ConfirmationDialog
        isSubmitting={isRejectingLicense}
        maxWidth={800}
        onClose={() => {
          setOpenConfirmationDialog(false)
        }}
        onSubmit={async () => {
          try {
            const staff = await rejectLicense({ licenseId: license._id, staffId: authUser._id }).unwrap()
            dispatch(setAuthUser(staff))
            setOpenConfirmationDialog(false)
            navigate('/licenses')
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.license.reject.error')), { variant: 'error' })
            setOpenConfirmationDialog(false)
          }
        }}
        open={openConfirmationDialog}
        title={t('dialog.confirmation.rejectLicense.title')}
        message={t('dialog.confirmation.rejectLicense.message')}
      />
    </PageContainer>
  )
}
